import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Chip,
  IconButton,
  Modal,
  Fade,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState, useEffect, useRef } from "react";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CloseIcon from "@mui/icons-material/Close";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ImageIcon from "@mui/icons-material/Image";

export default function FeaturesShowcaseComponent({ theme }) {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const sectionRef = useRef(null);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const handleFeatureClick = (feature) => {
    setSelectedFeature(feature);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setTimeout(() => setSelectedFeature(null), 200);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Listener separado para o modal
  useEffect(() => {
    const handleOpenFeatureModal = (event) => {
      const featureId = event.detail.featureId;
      const feature = features.find(f => f.id === featureId);
      if (feature) {
        handleFeatureClick(feature);
      }
    };

    window.addEventListener('openFeatureModal', handleOpenFeatureModal);

    return () => {
      window.removeEventListener('openFeatureModal', handleOpenFeatureModal);
    };
  }, []);

  const features = [
    {
      id: 'auditoria',
      title: 'Auditoria Inteligente',
      description: 'Monitore e analise todos os aspectos do seu negócio com relatórios detalhados e insights acionáveis.',
      icon: <AssessmentIcon sx={{ fontSize: 32 }} />,
      color: '#1A73E8',
      gradient: 'linear-gradient(135deg, #1A73E8 0%, #1557B0 100%)',
      image: require('../../../assets/auditoria/auditoria_media_png.png'),
      video: require('../../../assets/auditoria/auditoria_media_video.MP4'),
      stats: 'Redução de 60% no tempo de análise',
      features: ['Relatórios automáticos', 'Análise de performance', 'Alertas inteligentes']
    },
    {
      id: 'automacoes',
      title: 'Automações Avançadas',
      description: 'Automatize processos repetitivos e libere tempo para focar no crescimento estratégico do seu negócio.',
      icon: <AutoFixHighIcon sx={{ fontSize: 32 }} />,
      color: '#FF7323',
      gradient: 'linear-gradient(135deg, #FF7323 0%, #D85A00 100%)',
      image: require('../../../assets/automacoes/automacoes_png.png'),
      video: require('../../../assets/automacoes/automacoes_video.MP4'),
      stats: 'Economia de 40 horas semanais',
      features: ['Automação de preços', 'Gestão de estoque', 'Processamento de pedidos']
    },
    {
      id: 'etiquetas',
      title: 'Gestão de Etiquetas',
      description: 'Organize e gerencie etiquetas de produtos de forma eficiente com sistema automatizado.',
      icon: <LocalOfferIcon sx={{ fontSize: 32 }} />,
      color: '#38A169',
      gradient: 'linear-gradient(135deg, #38A169 0%, #2F855A 100%)',
      image: require('../../../assets/etiquetas/etiquetas_media_png.png'),
      video: require('../../../assets/etiquetas/etiquetas_media.MOV'),
      stats: 'Aumento de 35% na organização',
      features: ['Etiquetagem automática', 'Categorização inteligente', 'Controle de qualidade']
    },
    {
      id: 'fretegratis',
      title: 'Otimização de Frete',
      description: 'Estratégias inteligentes para frete grátis que aumentam conversões sem comprometer a margem.',
      icon: <LocalShippingIcon sx={{ fontSize: 32 }} />,
      color: '#9C27B0',
      gradient: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
      image: require('../../../assets/fretegratis/fretegratis_png.png'),
      video: require('../../../assets/fretegratis/fretegratis_video.MP4'),
      stats: 'Aumento de 25% nas conversões',
      features: ['Cálculo otimizado', 'Estratégias de pricing', 'Análise de ROI']
    },
    {
      id: 'margem',
      title: 'Análise de Margem',
      description: 'Monitore e otimize suas margens de lucro com precisão e tome decisões baseadas em dados reais.',
      icon: <TrendingUpIcon sx={{ fontSize: 32 }} />,
      color: '#E91E63',
      gradient: 'linear-gradient(135deg, #E91E63 0%, #C2185B 100%)',
      image: require('../../../assets/margem/margem_media_png.png'),
      video: require('../../../assets/margem/margem_media.MP4'),
      stats: 'Aumento de 30% na lucratividade',
      features: ['Análise em tempo real', 'Otimização de preços', 'Alertas de margem baixa']
    }
  ];

  return (
    <Box
      ref={sectionRef}
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
        background: `
          linear-gradient(135deg, 
            rgba(255, 255, 255, 1) 0%, 
            rgba(245, 248, 252, 1) 50%, 
            rgba(237, 242, 247, 1) 100%
          )
        `,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background Elements */}
      <Box
        sx={{
          position: "absolute",
          top: 200,
          left: -150,
          width: 300,
          height: 300,
          background: "radial-gradient(circle, rgba(255, 115, 35, 0.04) 0%, transparent 70%)",
          borderRadius: "50%",
          zIndex: 0,
        }}
      />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        {/* Header Section */}
        <Box
          id="features-header"
          sx={{
            textAlign: "center",
            mb: 8,
            transform: isVisible ? "translateY(0)" : "translateY(50px)",
            opacity: isVisible ? 1 : 0,
            transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <Chip
            label="⚡ Funcionalidades Poderosas"
            sx={{
              mb: 3,
              backgroundColor: "rgba(255, 115, 35, 0.1)",
              color: theme.palette.primary.main,
              fontWeight: 600,
              fontSize: "1rem",
              padding: "12px 20px",
              borderRadius: "50px",
              border: "1px solid rgba(255, 115, 35, 0.2)",
            }}
          />

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3.2rem" },
              fontWeight: 800,
              mb: 4,
              background: "linear-gradient(135deg, #FF7323 0%, #D85A00 50%, #1A73E8 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Veja Nossas Funcionalidades em Ação
          </Typography>

          <Typography
            variant="h5"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: { xs: "1rem", sm: "1.2rem", md: "1.4rem" },
              maxWidth: "800px",
              mx: "auto",
              lineHeight: 1.6,
              mb: 2,
            }}
          >
            Conheça as ferramentas que vão revolucionar seu e-commerce
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.tertiary,
              fontSize: "1rem",
              maxWidth: "600px",
              mx: "auto",
            }}
          >
            Clique em qualquer card para ver uma demonstração detalhada
          </Typography>
        </Box>

        {/* Features Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)'
            },
            gap: 3,
            mb: 8,
          }}
        >
          {features.map((feature, index) => (
            <Card
              key={feature.id}
              onClick={() => handleFeatureClick(feature)}
              sx={{
                cursor: 'pointer',
                borderRadius: "32px",
                border: `2px solid ${feature.color}20`,
                boxShadow: `0px 12px 40px ${feature.color}15`,
                transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                background: `linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.85) 100%)`,
                backdropFilter: "blur(25px)",
                transform: isVisible ? "translateY(0)" : "translateY(50px)",
                opacity: isVisible ? 1 : 0,
                transitionDelay: `${index * 150}ms`,
                position: 'relative',
                overflow: 'hidden',
                "&:hover": {
                  transform: "translateY(-20px) scale(1.03)",
                  boxShadow: `0px 25px 80px ${feature.color}25`,
                  border: `2px solid ${feature.color}40`,
                  "& .feature-image": {
                    transform: "scale(1.08)",
                  },
                  "& .play-button": {
                    opacity: 1,
                    transform: "scale(1.2)",
                    background: feature.gradient,
                  },
                  "& .feature-glow": {
                    opacity: 0.8,
                    transform: "scale(1.2)",
                  },
                  "& .stats-badge": {
                    transform: "scale(1.1) translateY(-5px)",
                    boxShadow: `0 8px 25px ${feature.color}40`,
                  }
                },
              }}
            >
              {/* Background Glow Effect */}
              <Box
                className="feature-glow"
                sx={{
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: 120,
                  height: 120,
                  background: `radial-gradient(circle, ${feature.color}20 0%, transparent 70%)`,
                  borderRadius: '50%',
                  opacity: 0.4,
                  transition: 'all 0.5s ease',
                  zIndex: 0,
                }}
              />

              <Box sx={{ position: 'relative', zIndex: 1 }}>
                {/* Enhanced Image Section */}
                <Box
                  sx={{
                    height: 240,
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '32px 32px 20px 20px',
                    background: `linear-gradient(135deg, ${feature.color}05 0%, transparent 100%)`,
                  }}
                >
                  {/* Gradient Overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `linear-gradient(180deg, transparent 0%, ${feature.color}10 100%)`,
                      zIndex: 2,
                    }}
                  />

                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="feature-image"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.5s ease',
                    }}
                  />
                  
                  {/* Enhanced Play Button */}
                  <Box
                    className="play-button"
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      opacity: 0,
                      transition: 'all 0.4s ease',
                      background: 'rgba(0, 0, 0, 0.8)',
                      borderRadius: '50%',
                      width: 70,
                      height: 70,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      zIndex: 5,
                    }}
                  >
                    <PlayArrowIcon sx={{ color: 'white', fontSize: 36, ml: 0.5 }} />
                  </Box>

                  {/* Enhanced Feature Icon */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 20,
                      left: 20,
                      width: 56,
                      height: 56,
                      borderRadius: '16px',
                      background: feature.gradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      boxShadow: `0 8px 25px ${feature.color}40`,
                      border: '2px solid rgba(255, 255, 255, 0.2)',
                      zIndex: 3,
                    }}
                  >
                    {feature.icon}
                  </Box>

                  {/* Stats Badge */}
                  <Box
                    className="stats-badge"
                    sx={{
                      position: 'absolute',
                      top: 20,
                      right: 20,
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(15px)',
                      borderRadius: '16px',
                      px: 2,
                      py: 1,
                      border: `1px solid ${feature.color}30`,
                      transition: 'all 0.3s ease',
                      zIndex: 3,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: feature.color,
                        fontWeight: 800,
                        fontSize: '0.8rem',
                        textShadow: `0 1px 2px ${feature.color}20`,
                      }}
                    >
                      {feature.stats}
                    </Typography>
                  </Box>
                </Box>

                {/* Enhanced Content Section */}
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        mb: 1.5,
                        color: theme.palette.text.primary,
                        fontSize: "1.4rem",
                        lineHeight: 1.2,
                      }}
                    >
                      {feature.title}
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.text.secondary,
                        lineHeight: 1.7,
                        mb: 3,
                        fontSize: "1rem",
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Box>

                  {/* Enhanced Feature Tags */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: 600,
                        mb: 1,
                        fontSize: "0.9rem",
                      }}
                    >
                      Principais recursos:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                      {feature.features.map((feat, idx) => (
                        <Chip
                          key={idx}
                          label={feat}
                          size="small"
                          sx={{
                            background: `linear-gradient(135deg, ${feature.color}15 0%, ${feature.color}08 100%)`,
                            color: feature.color,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            border: `1px solid ${feature.color}20`,
                            '&:hover': {
                              background: `linear-gradient(135deg, ${feature.color}25 0%, ${feature.color}15 100%)`,
                            }
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>

                  {/* Call to Action */}
                  <Box
                    sx={{
                      textAlign: 'center',
                      pt: 2,
                      borderTop: `1px solid ${feature.color}15`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: feature.color,
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      <PlayArrowIcon sx={{ fontSize: 16 }} />
                      Clique para ver demonstração
                    </Typography>
                  </Box>
                </CardContent>
              </Box>
            </Card>
          ))}
        </Box>

      </Container>

      {/* Enhanced Modal for Feature Details */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        closeAfterTransition
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          backdropFilter: 'blur(8px)',
        }}
      >
        <Fade in={modalOpen}>
          <Box
            sx={{
              maxWidth: isMobile ? '95vw' : '1100px',
              width: '100%',
              maxHeight: '95vh',
              background: `linear-gradient(145deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%)`,
              backdropFilter: 'blur(25px)',
              borderRadius: '32px',
              overflow: 'hidden',
              boxShadow: selectedFeature ? `0 30px 100px ${selectedFeature.color}20, 0 10px 40px rgba(0,0,0,0.2)` : '0 25px 80px rgba(0,0,0,0.3)',
              position: 'relative',
              border: selectedFeature ? `2px solid ${selectedFeature.color}20` : '2px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            {selectedFeature && (
              <>
                {/* Enhanced Close Button */}
                <IconButton
                  onClick={handleCloseModal}
                  sx={{
                    position: 'absolute',
                    top: 20,
                    right: 20,
                    zIndex: 10,
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    width: 44,
                    height: 44,
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 1)',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>

                {/* Enhanced Video Section */}
                <Box 
                  sx={{ 
                    position: 'relative', 
                    height: isMobile ? 280 : 500,
                    background: `linear-gradient(135deg, ${selectedFeature.color}05 0%, transparent 100%)`,
                  }}
                >
                  {/* Video Background Glow */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -20,
                      left: -20,
                      right: -20,
                      bottom: -20,
                      background: `radial-gradient(ellipse at center, ${selectedFeature.color}10 0%, transparent 70%)`,
                      zIndex: 0,
                    }}
                  />
                  
                  <video
                    controls
                    autoPlay
                    muted
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '32px 32px 0 0',
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    <source src={selectedFeature.video} type="video/mp4" />
                    Seu navegador não suporta vídeos.
                  </video>

                  {/* Feature Badge Overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 24,
                      left: 24,
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(15px)',
                      borderRadius: '20px',
                      px: 3,
                      py: 1.5,
                      border: `2px solid ${selectedFeature.color}30`,
                      zIndex: 5,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: selectedFeature.color,
                        fontWeight: 800,
                        fontSize: '1.1rem',
                        textShadow: `0 1px 2px ${selectedFeature.color}20`,
                      }}
                    >
                      {selectedFeature.stats}
                    </Typography>
                  </Box>
                </Box>

                {/* Enhanced Content Section */}
                <Box sx={{ p: { xs: 3, md: 5 } }}>
                  {/* Header with Icon */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: '20px',
                        background: selectedFeature.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        mr: 3,
                        boxShadow: `0 8px 25px ${selectedFeature.color}40`,
                        border: '3px solid rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      {selectedFeature.icon}
                    </Box>
                    <Box flex={1}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 800,
                          color: theme.palette.text.primary,
                          fontSize: { xs: '1.8rem', md: '2.2rem' },
                          mb: 1,
                          lineHeight: 1.2,
                        }}
                      >
                        {selectedFeature.title}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: selectedFeature.color,
                          fontWeight: 600,
                          fontSize: '1.1rem',
                        }}
                      >
                        Funcionalidade Premium • Resultados Garantidos
                      </Typography>
                    </Box>
                  </Box>

                  {/* Enhanced Description */}
                  <Box
                    sx={{
                      background: `linear-gradient(135deg, ${selectedFeature.color}05 0%, rgba(255, 255, 255, 0.8) 100%)`,
                      borderRadius: '20px',
                      p: 3,
                      mb: 4,
                      border: `1px solid ${selectedFeature.color}15`,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.text.secondary,
                        lineHeight: 1.8,
                        fontSize: '1.15rem',
                        fontWeight: 400,
                      }}
                    >
                      {selectedFeature.description}
                    </Typography>
                  </Box>

                  {/* Enhanced Features Grid */}
                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        mb: 3,
                        color: theme.palette.text.primary,
                        fontSize: '1.4rem',
                      }}
                    >
                      🎯 Recursos Inclusos:
                    </Typography>
                    
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                        gap: 2,
                      }}
                    >
                      {selectedFeature.features.map((feat, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            background: `linear-gradient(135deg, ${selectedFeature.color}15 0%, ${selectedFeature.color}08 100%)`,
                            borderRadius: '16px',
                            p: 2.5,
                            border: `2px solid ${selectedFeature.color}20`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: `0 8px 25px ${selectedFeature.color}25`,
                              background: `linear-gradient(135deg, ${selectedFeature.color}25 0%, ${selectedFeature.color}15 100%)`,
                            }
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: selectedFeature.color,
                              fontWeight: 700,
                              fontSize: '0.9rem',
                              textAlign: 'center',
                            }}
                          >
                            ✅ {feat}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  {/* Call to Action Footer */}
                  <Box
                    sx={{
                      textAlign: 'center',
                      pt: 3,
                      borderTop: `2px solid ${selectedFeature.color}15`,
                      background: `linear-gradient(90deg, transparent 0%, ${selectedFeature.color}05 50%, transparent 100%)`,
                      borderRadius: '16px',
                      mt: 3,
                      p: 3,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: selectedFeature.color,
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        mb: 1,
                      }}
                    >
                      🚀 Pronto para implementar esta funcionalidade?
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.95rem',
                      }}
                    >
                      Entre em contato conosco e veja como essa ferramenta pode transformar seu negócio
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
}